/**
 * SupportedPlatforms Component
 * 
 * Displays a grid of supported video platforms with their logos.
 * Features a modern, animated design with hover effects and modal for all platforms.
 * 
 * Features:
 * - Responsive grid layout with real platform logos
 * - Animated entrance for each platform
 * - Interactive hover effects
 * - Modal to show all supported platforms
 * - i18n support for all text content
 * - Optimized for both light and dark modes
 * - Local search functionality
 */

import React, { useState, useEffect } from "react";
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import Link from "next/link";
import Image from "next/image";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronRight } from 'lucide-react';
import { platformSlugs } from "@/config/platformSlugs";
import { getPlatformInfo, getAllPlatforms } from '@/lib/utils/platformUtils';

// Định nghĩa interface cho platform
type Platform = {
  id: string;
  name: string;
  domain: string;
  logo: string;
  slug: string;
  featured?: boolean;
  fullySupported?: boolean;
  type?: string;
};

// Lấy danh sách tất cả các nền tảng
const allPlatforms = getAllPlatforms().map(platform => ({
  ...platform,
  logo: platform.logo || `/platforms/${platform.id}.svg`
}));

// Lọc các nền tảng nổi bật (featured)
const featuredPlatforms = allPlatforms.filter(p => p.featured);

const SupportedPlatforms = () => {
  const { t } = useTranslation('platforms');
  const router = useRouter();
  const { locale, defaultLocale } = router;
  const [hoveredPlatform, setHoveredPlatform] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Helper: get URL for a platform
  const getPlatformUrl = (platformId: string) => {
    const slug = platformSlugs[platformId];
    if (!slug) return "#";
    // Nếu locale là default thì không prefix, còn lại thì prefix /vi/...
    const prefix = locale && defaultLocale && locale !== defaultLocale ? `/${locale}` : "";
    return `${prefix}/${slug}`;
  };

  // Sử dụng dữ liệu từ platformUtils
  const displayedPlatforms = searchTerm
    ? allPlatforms.filter(platform => 
        getPlatformInfo(platform.id)?.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        platform.id.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : allPlatforms;

  // Only prioritize loading the first few images
  const shouldPriorityLoad = (index: number) => index < 4;

  // Animation variants for staggered entrance
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  
  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.5 }
    }
  };
  
  // Modal animation variants
  const modalVariants = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: { 
      opacity: 1, 
      scale: 1,
      transition: { duration: 0.3 }
    },
    exit: {
      opacity: 0,
      scale: 0.9,
      transition: { duration: 0.2 }
    }
  };
  
  // Open/close modal
  const toggleModal = () => setIsModalOpen(!isModalOpen);

  return (
    <section className="py-16 px-4 bg-gray-50 dark:bg-gray-900/70" id="platforms">
      <div className="container mx-auto max-w-6xl">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold mb-4 gradient-text">
            {t('title')}
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
            {t('description')}
          </p>
        </div>
        
        <motion.div 
          className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-6 md:gap-8"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
        >
          {featuredPlatforms.map((platform: {id: string, logo: string}, index: number) => (
            <motion.div
              key={platform.id}
              className="card-hover"
              variants={itemVariants}
              onMouseEnter={() => setHoveredPlatform(platform.id)}
              onMouseLeave={() => setHoveredPlatform(null)}
            >
              <Link href={getPlatformUrl(platform.id)} className="flex flex-col items-center bg-white dark:bg-gray-800 rounded-xl shadow-md p-6 h-full group" prefetch={false}>
                <div className={`relative w-16 h-16 mb-4 rounded-lg overflow-hidden transition-all duration-300 ${hoveredPlatform === platform.id ? 'scale-110' : ''}`}>
                  <div className="absolute inset-0 bg-gradient-to-br from-primary/10 to-accent/10 rounded-lg"></div>
                  <Image
                    src={platform.logo}
                    alt={getPlatformInfo(platform.id)?.name || platform.id}
                    width={64}
                    height={64}
                    className="object-contain p-3 relative z-10"
                    style={{ width: 'auto', height: 'auto' }}
                    unoptimized
                  />
                </div>
                <span className="text-base font-medium text-gray-900 dark:text-gray-100 group-hover:underline">
                  {getPlatformInfo(platform.id)?.name || platform.id}
                </span>
              </Link>
            </motion.div>
          ))}
        </motion.div>
        
        <div className="text-center mt-10">
          <button 
            className="inline-flex items-center px-6 py-3 rounded-xl bg-primary hover:bg-primary/90 text-primary-foreground transition-colors button-hover"
            onClick={toggleModal}
          >
            {t('viewAll')}
            <ChevronRight className="w-5 h-5 ml-2" />
          </button>
        </div>
      </div>
      
      {/* All Platforms Modal */}
      <AnimatePresence>
        {isModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <motion.div 
              className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-4xl w-full max-h-[80vh] overflow-hidden"
              variants={modalVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                  {t('allSupportedTitle')}
                </h3>
                <button 
                  className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  onClick={toggleModal}
                  aria-label="Close modal"
                >
                  <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                </button>
              </div>
              
              <div className="p-6 overflow-y-auto max-h-[calc(80vh-120px)]">
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                  {displayedPlatforms.map((platform: {id: string, logo: string}) => (
                    <Link
                      key={platform.id}
                      href={getPlatformUrl(platform.id)}
                      className="flex flex-col items-center p-4 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group"
                      prefetch={false}
                    >
                      <div className="w-12 h-12 mb-2">
                        <Image
                          src={platform.logo}
                          alt={getPlatformInfo(platform.id)?.name || platform.id}
                          width={48}
                          height={48}
                          className="object-contain"
                          style={{ width: 'auto', height: 'auto' }}
                          unoptimized
                        />
                      </div>
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100 text-center group-hover:underline">
                        {getPlatformInfo(platform.id)?.name || platform.id}
                      </span>
                    </Link>
                  ))}
                </div>
              </div>
              
              <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end">
                <button 
                  className="px-4 py-2 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  onClick={toggleModal}
                >
                  {t('close')}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default SupportedPlatforms;
